import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../utils/currency';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from '../components/NotificationBell';
import Footer from '../components/Footer';
import api from '../services/api';
import GymLogo from '../components/GymLogo';
import {
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BellIcon,
  IdentificationIcon,
  CreditCardIcon,
  XMarkIcon,
  ChatBubbleLeftIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ArrowUpIcon,
  FireIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const { formatCurrency } = useCurrency(user?.gym_id);
  const { changeAccentColor } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceMessage, setAttendanceMessage] = useState(null);
  const [gymStatus, setGymStatus] = useState(null);
  const [showGymStatus, setShowGymStatus] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchGymStatus();

    const statusInterval = setInterval(() => {
      fetchGymStatus();
    }, 10000);

    if (location.state) {
      if (location.state.openTab) {
        setActiveTab(location.state.openTab);
      }
      if (location.state.attendanceSuccess) {
        setAttendanceMessage({
          type: 'success',
          text: 'Attendance marked successfully!',
          data: location.state.attendanceData
        });
      } else if (location.state.attendanceError) {
        setAttendanceMessage({
          type: 'error',
          text: location.state.attendanceError
        });
      }
      window.history.replaceState({}, document.title);
    }

    return () => clearInterval(statusInterval);
  }, [location.state]);

  const fetchGymStatus = async () => {
    try {
      const response = await api.get('/api/members/gym-status');
      setGymStatus(response.data);
      setShowGymStatus(response.data.show_gym_status);
    } catch (err) {
      console.error('Failed to fetch gym status:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/members/me');
      setDashboardData(response.data);
      const gymId = response.data?.member?.gym_id;
      const gymColor = response.data?.gym?.accent_color || (gymId ? localStorage.getItem(`gym_accent_color_${gymId}`) : null);
      if (gymColor) {
        changeAccentColor(gymColor);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayAndUpgradePlan = async (plan) => {
    try {
      setPaying(true);
      setPaymentMsg('');
      const scriptLoaded = await loadRazorpayScript();

      const processPaymentInBackend = async (razorpayPaymentId = null) => {
        const res = await api.post('/api/payments/member-pay', {
          gym_id: user?.gym_id || dashboardData?.member?.gym_id || 1,
          member_id: dashboardData?.member?.id,
          amount: plan.price,
          payment_method: razorpayPaymentId ? 'Razorpay Gateway' : 'GPay / PhonePe UPI'
        });
        setPaymentMsg(res.data.message || `Plan upgraded to ${plan.plan_name}!`);
        fetchDashboardData();
        setTimeout(() => setPaymentMsg(''), 6000);
      };

      if (!scriptLoaded || !window.Razorpay) {
        if (window.confirm(`Confirm paying ₹${plan.price} to upgrade/renew your ${plan.plan_name} membership?`)) {
          await processPaymentInBackend(`RZP-MEM-TEST-${Date.now()}`);
        }
        return;
      }

      const options = {
        key: 'rzp_test_1DP5mmOlF5G5ag',
        amount: plan.price * 100,
        currency: 'INR',
        name: dashboardData?.gym?.name || 'Gym Membership Upgrade',
        description: `Upgrade / Renewal: ${plan.plan_name}`,
        handler: async function (response) {
          await processPaymentInBackend(response.razorpay_payment_id);
        },
        prefill: {
          name: `${dashboardData?.member?.first_name || ''} ${dashboardData?.member?.last_name || ''}`.trim(),
          email: dashboardData?.member?.email || '',
          contact: dashboardData?.member?.phone || ''
        },
        theme: {
          color: '#ea580c'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      alert('Payment processing error: ' + (err.response?.data?.error || err.message));
    } finally {
      setPaying(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    return new Date(dateTimeString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your Member Portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl max-w-md">
          <p className="font-semibold">Error</p>
          <p className="mt-2">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { member, gym, attendance, payments, membership, workout_plans = [], diet_plans = [] } = dashboardData;
  const availablePlans = membership?.available_plans || [];

  // Determine current member plan price
  const currentPlan = availablePlans.find(p => p.plan_name === member.membership_plan_name);
  const currentPrice = currentPlan ? currentPlan.price : 0;

  // Filter plans: ONLY show plans with price >= currentPrice for Upgrade/Renew
  const upgradePlans = availablePlans.filter(p => p.price >= currentPrice);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Attendance Alert Banner */}
      {attendanceMessage && (
        <div className={`${
          attendanceMessage.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        } border p-4 mx-4 mt-4 rounded-xl`}>
          <div className="flex items-start gap-3">
            {attendanceMessage.type === 'success' ? (
              <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            ) : (
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-semibold">{attendanceMessage.text}</p>
              {attendanceMessage.data && (
                <p className="text-sm mt-1">
                  Check-in time: {new Date(attendanceMessage.data.check_in_time).toLocaleTimeString()}
                </p>
              )}
            </div>
            <button
              onClick={() => setAttendanceMessage(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <GymLogo 
                className="w-12 h-12" 
                showGymName={true}
                gymNameClassName="text-sm font-medium text-gray-900"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Welcome, {member.first_name}!</h1>
                <p className="text-xs text-gray-500">Member Portal • Gym ID #{user?.gym_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {showGymStatus && gymStatus && (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white ${
                  gymStatus.operational_status === 'Open' 
                    ? 'bg-green-500' 
                    : 'bg-red-500'
                }`}>
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                  {gymStatus.operational_status === 'Open' ? '🟢 GYM OPEN' : '🔴 GYM CLOSED'}
                </div>
              )}
              <NotificationBell />
              <button
                onClick={() => navigate('/member/settings')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Cog6ToothIcon className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={handleLogout}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-2 overflow-x-auto pb-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-4 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'overview'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserIcon className="w-4 h-4" /> Overview
          </button>

          <button
            onClick={() => setActiveTab('upgrade_plan')}
            className={`pb-3 px-4 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'upgrade_plan'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <SparklesIcon className="w-4 h-4 text-purple-600" /> Pay & Upgrade Plan
            {membership?.is_expiring_soon && (
              <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Renew</span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('workout_diet')}
            className={`pb-3 px-4 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'workout_diet'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FireIcon className="w-4 h-4 text-emerald-600" /> Workout & Diet Plan
          </button>

          <button
            onClick={() => setActiveTab('attendance')}
            className={`pb-3 px-4 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'attendance'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CalendarIcon className="w-4 h-4" /> Attendance
          </button>

          <button
            onClick={() => setActiveTab('payments')}
            className={`pb-3 px-4 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'payments'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CurrencyDollarIcon className="w-4 h-4" /> Payments
          </button>

          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-4 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'profile'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <IdentificationIcon className="w-4 h-4" /> Profile
          </button>

          <button
            onClick={() => navigate('/member/messages')}
            className="pb-3 px-4 font-bold text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
          >
            <ChatBubbleLeftIcon className="w-4 h-4" /> Messages
          </button>
        </div>

        {paymentMsg && (
          <div className="mb-6 bg-emerald-100 border border-emerald-300 text-emerald-900 p-4 rounded-2xl text-xs font-bold flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
            {paymentMsg}
          </div>
        )}

        {/* Tab 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Membership Digital Card */}
            <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 rounded-3xl p-6 text-white shadow-xl">
              <div className="flex justify-between items-start">
                <div>
                  <span className="bg-white/20 text-white text-[10px] font-extrabold uppercase px-3 py-1 rounded-full border border-white/20">
                    Official Digital ID Card
                  </span>
                  <h2 className="text-2xl font-black mt-2">{member.membership_plan_name || 'Standard Gym Plan'}</h2>
                </div>
                <IdentificationIcon className="w-10 h-10 text-orange-200 opacity-90" />
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 bg-black/10 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                <div>
                  <p className="text-orange-200 text-[10px] uppercase font-bold tracking-wider">Member ID</p>
                  <p className="font-mono text-lg font-black">{member.member_id}</p>
                </div>
                <div>
                  <p className="text-orange-200 text-[10px] uppercase font-bold tracking-wider">Status</p>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    member.status === 'Active' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {member.status}
                  </span>
                </div>
                <div>
                  <p className="text-orange-200 text-[10px] uppercase font-bold tracking-wider">Joined Date</p>
                  <p className="font-semibold text-sm">{formatDate(member.membership_start_date)}</p>
                </div>
                <div>
                  <p className="text-orange-200 text-[10px] uppercase font-bold tracking-wider">Expiry Date</p>
                  <p className="font-semibold text-sm">{formatDate(member.membership_end_date)}</p>
                </div>
              </div>

              {membership.is_expiring_soon && (
                <div className="mt-4 bg-red-900/60 border border-red-400/40 rounded-2xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-300 shrink-0" />
                    <p className="text-xs font-bold text-red-100">
                      Your membership expires in <strong className="text-white text-sm">{membership.remaining_days} days</strong>. Renew now to avoid interruption!
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('upgrade_plan')}
                    className="bg-white text-orange-600 font-extrabold px-4 py-2 rounded-xl text-xs shadow-md hover:bg-orange-50 shrink-0"
                  >
                    Renew Plan Now →
                  </button>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex items-center gap-4">
                <div className="bg-blue-100 p-3.5 rounded-2xl">
                  <CalendarIcon className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Total Check-Ins</p>
                  <p className="text-3xl font-black text-gray-900">{attendance.total_visits}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex items-center gap-4">
                <div className="bg-emerald-100 p-3.5 rounded-2xl">
                  <CurrencyDollarIcon className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Total Fees Paid</p>
                  <p className="text-3xl font-black text-gray-900">{formatCurrency(payments.total_paid)}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex items-center gap-4">
                <div className="bg-purple-100 p-3.5 rounded-2xl">
                  <ClockIcon className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Monthly Visits</p>
                  <p className="text-3xl font-black text-gray-900">{attendance.monthly_visits}</p>
                </div>
              </div>
            </div>

            {/* Today's Check-In Status - 3 States */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="text-md font-bold text-gray-900 flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-red-600" /> Today's Workout & Attendance Status
              </h3>
              
              {attendance.today_status === 'Checked In' && attendance.is_timed_out ? (
                /* State 2: Time Out (RED WARNING) */
                <div className="p-5 rounded-2xl bg-red-100/90 border-2 border-red-500 flex items-center justify-between gap-4 shadow-md">
                  <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-2xl bg-red-600 text-white animate-bounce">
                      <ExclamationTriangleIcon className="w-8 h-8" />
                    </div>
                    <div>
                      <span className="bg-red-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        🔴 TIME OUT / SESSION EXPIRED
                      </span>
                      <p className="text-xl font-black text-red-900 mt-1">Workout Session Time Limit Reached</p>
                      <p className="text-xs font-bold text-red-800">
                        {attendance.last_check_in ? `Checked In: ${formatDateTime(attendance.last_check_in)}` : 'Overtime Limit Reached'}
                      </p>
                    </div>
                  </div>
                  <span className="bg-red-700 text-white px-4 py-2 rounded-xl text-xs font-black shadow">
                    Please Exit Gym
                  </span>
                </div>
              ) : attendance.today_status === 'Checked In' ? (
                /* State 1: Active Checked In (GREEN) */
                <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-300 flex items-center justify-between gap-4 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-2xl bg-emerald-500 text-white">
                      <CheckCircleIcon className="w-8 h-8" />
                    </div>
                    <div>
                      <span className="bg-emerald-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        🟢 ACTIVE INSIDE GYM
                      </span>
                      <p className="text-xl font-black text-emerald-950 mt-1">Currently Working Out</p>
                      {attendance.last_check_in && (
                        <p className="text-xs font-semibold text-emerald-800">
                          Check-In Time: {formatDateTime(attendance.last_check_in)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs font-bold text-emerald-700">Gym Session Active</span>
                  </div>
                </div>
              ) : (
                /* State 3: Exited / Checked Out */
                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-2xl bg-gray-200 text-gray-700">
                      <ClockIcon className="w-8 h-8" />
                    </div>
                    <div>
                      <span className="bg-gray-200 text-gray-700 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        🏁 EXITED GYM / NOT CHECKED IN
                      </span>
                      <p className="text-lg font-bold text-gray-900 mt-1">
                        {attendance.today_status === 'Checked Out' ? 'Checked Out for Today' : 'Not Checked In Today'}
                      </p>
                      {attendance.last_check_in && (
                        <p className="text-xs text-gray-500">
                          Last Activity: {formatDateTime(attendance.last_check_in)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: PAY & UPGRADE MEMBERSHIP PLAN */}
        {activeTab === 'upgrade_plan' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white p-6 rounded-3xl shadow-xl space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <span className="bg-purple-500/30 text-purple-200 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider border border-purple-400/30">
                    Upgrade & Renewal Portal
                  </span>
                  <h2 className="text-2xl font-black flex items-center gap-2 mt-2">
                    <SparklesIcon className="w-7 h-7 text-amber-400" /> Gym Membership Options
                  </h2>
                  <p className="text-xs text-purple-200 mt-1">
                    Current Plan: <strong className="text-white">{member.membership_plan_name || 'Standard Plan'}</strong> • Expiry Date: <strong className="text-amber-300">{formatDate(member.membership_end_date)}</strong> (<strong className="text-emerald-300">{membership?.remaining_days ?? 0} Days Remaining</strong>)
                  </p>
                </div>

                {currentPlan && (
                  <button
                    disabled={paying}
                    onClick={() => handlePayAndUpgradePlan(currentPlan)}
                    className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 shrink-0"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    {paying ? 'Opening Payment...' : `Renew ${currentPlan.plan_name} (₹${currentPlan.price})`}
                  </button>
                )}
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-2xl border border-white/10 text-xs text-emerald-200 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-amber-300 shrink-0" />
                <span>
                  ⚡ <strong>Cumulative Expiry Feature:</strong> Renewing anytime adds full plan duration (+30 Days) onto your remaining days (e.g. {membership?.remaining_days ?? 0} remaining days + 30 days = <strong>{(membership?.remaining_days ?? 0) + 30} total days</strong> expiry date)!
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upgradePlans.length === 0 ? (
                <div className="col-span-full bg-white border border-gray-200 p-8 rounded-3xl text-center text-gray-500">
                  No upgrade plans available at this time. Please contact your gym manager!
                </div>
              ) : (
                upgradePlans.map((plan) => {
                  const isCurrent = plan.plan_name === member.membership_plan_name;
                  return (
                    <div
                      key={plan.id}
                      className={`bg-white border p-6 rounded-3xl shadow-sm hover:shadow-md transition-all flex flex-col justify-between ${
                        isCurrent ? 'border-2 border-orange-500 relative' : 'border-gray-200'
                      }`}
                    >
                      {isCurrent && (
                        <span className="absolute -top-3 right-4 bg-orange-600 text-white text-[10px] uppercase font-black px-3 py-0.5 rounded-full">
                          Your Active Plan
                        </span>
                      )}

                      <div>
                        <span className="text-xs uppercase font-extrabold text-orange-600">{plan.plan_name}</span>
                        <h3 className="text-3xl font-black text-gray-900 mt-1">₹{plan.price} <span className="text-xs text-gray-500 font-normal">/ {plan.duration} Days</span></h3>
                        <p className="text-xs text-gray-600 mt-3">{plan.description || 'Full access to gym equipment and workout guidance.'}</p>
                      </div>

                      <button
                        disabled={paying}
                        onClick={() => handlePayAndUpgradePlan(plan)}
                        className={`w-full mt-6 py-3 font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 ${
                          isCurrent
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                        }`}
                      >
                        <CreditCardIcon className="w-4 h-4" />
                        {paying
                          ? 'Opening Payment...'
                          : isCurrent
                          ? `Renew ${plan.plan_name} (₹${plan.price})`
                          : `Upgrade to ${plan.plan_name} (₹${plan.price})`}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Tab 3: WORKOUT & DIET PLAN */}
        {activeTab === 'workout_diet' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 text-white p-6 rounded-3xl shadow-xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black flex items-center gap-2">
                  <FireIcon className="w-6 h-6 text-amber-400" /> Customized Workout & Diet Schedule
                </h2>
                <p className="text-xs text-emerald-200 mt-1">
                  Assigned by your certified gym trainer to reach your fitness goals.
                </p>
              </div>
            </div>

            {/* Workout Plans */}
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm space-y-4">
              <h3 className="text-md font-bold text-gray-900 flex items-center gap-2">
                <FireIcon className="w-5 h-5 text-orange-600" /> Assigned Workout Routines
              </h3>
              {workout_plans.length === 0 ? (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 text-center text-xs text-orange-800 space-y-2">
                  <p className="font-bold">No custom workout plan assigned yet.</p>
                  <p className="text-gray-600">Standard Daily Routine: 15 mins Cardio Warm-up • 3 Sets Chest Press • 3 Sets Lat Pulldown • 15 mins Cooldown.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {workout_plans.map((wp) => (
                    <div key={wp.id} className="border border-gray-200 p-4 rounded-2xl bg-gray-50 space-y-2">
                      <div className="flex justify-between items-center">
                        <h4 className="font-extrabold text-sm text-gray-900">{wp.plan_name}</h4>
                        <span className="text-[10px] bg-orange-100 text-orange-800 font-bold px-2 py-0.5 rounded">Assigned Routine</span>
                      </div>
                      <p className="text-xs text-gray-600">{wp.notes || 'Follow exercises as per trainer instruction.'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Diet Plans */}
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm space-y-4">
              <h3 className="text-md font-bold text-gray-900 flex items-center gap-2">
                <HeartIcon className="w-5 h-5 text-emerald-600" /> Daily Macro & Nutrition Diet Plan
              </h3>
              {diet_plans.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center text-xs text-emerald-900 space-y-2">
                  <p className="font-bold">Standard Recommended Fitness Diet:</p>
                  <div className="grid grid-cols-4 gap-2 max-w-md mx-auto pt-2">
                    <div className="bg-white p-2 rounded-xl font-bold border"><p className="text-[10px] text-gray-500">Calories</p><p className="text-emerald-700 font-black">2000 kcal</p></div>
                    <div className="bg-white p-2 rounded-xl font-bold border"><p className="text-[10px] text-gray-500">Protein</p><p className="text-emerald-700 font-black">150g</p></div>
                    <div className="bg-white p-2 rounded-xl font-bold border"><p className="text-[10px] text-gray-500">Carbs</p><p className="text-emerald-700 font-black">200g</p></div>
                    <div className="bg-white p-2 rounded-xl font-bold border"><p className="text-[10px] text-gray-500">Fats</p><p className="text-emerald-700 font-black">60g</p></div>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {diet_plans.map((dp) => (
                    <div key={dp.id} className="border border-emerald-200 p-4 rounded-2xl bg-emerald-50/50 space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-extrabold text-sm text-emerald-950">{dp.plan_name}</h4>
                        <span className="text-[10px] bg-emerald-200 text-emerald-900 font-bold px-2.5 py-0.5 rounded-full">Custom Macros</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        <div className="bg-white p-2 rounded-xl font-bold"><p className="text-[10px] text-gray-400">Calories</p><p className="text-gray-900">{dp.target_calories}k</p></div>
                        <div className="bg-white p-2 rounded-xl font-bold"><p className="text-[10px] text-gray-400">Protein</p><p className="text-emerald-600">{dp.protein_grams}g</p></div>
                        <div className="bg-white p-2 rounded-xl font-bold"><p className="text-[10px] text-gray-400">Carbs</p><p className="text-blue-600">{dp.carbs_grams}g</p></div>
                        <div className="bg-white p-2 rounded-xl font-bold"><p className="text-[10px] text-gray-400">Fats</p><p className="text-amber-600">{dp.fats_grams}g</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 4: ATTENDANCE */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm">
              <h3 className="text-md font-bold text-gray-900 mb-4">Attendance Check-In History</h3>
              {attendance.history.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-xs">No attendance records found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 font-bold text-gray-600">
                        <th className="p-3">Date</th>
                        <th className="p-3">Check In</th>
                        <th className="p-3">Check Out</th>
                        <th className="p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {attendance.history.map((record) => (
                        <tr key={record.id}>
                          <td className="p-3 font-semibold text-gray-900">{formatDate(record.attendance_date)}</td>
                          <td className="p-3 text-gray-700">{formatDateTime(record.check_in_time)}</td>
                          <td className="p-3 text-gray-700">{record.check_out_time ? formatDateTime(record.check_out_time) : '-'}</td>
                          <td className="p-3">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              record.status === 'Checked In' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 5: PAYMENTS */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Total Paid Fees</h3>
                <p className="text-3xl font-black text-emerald-600">{formatCurrency(payments.total_paid)}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Pending Balance</h3>
                <p className="text-3xl font-black text-orange-600">{formatCurrency(payments.pending_amount)}</p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm">
              <h3 className="text-md font-bold text-gray-900 mb-4">Payment Receipts & History</h3>
              {payments.history.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-xs">No payment receipts found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 font-bold text-gray-600">
                        <th className="p-3">Date</th>
                        <th className="p-3">Amount</th>
                        <th className="p-3">Method</th>
                        <th className="p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payments.history.map((payment) => (
                        <tr key={payment.id}>
                          <td className="p-3 text-gray-900">{formatDate(payment.payment_date)}</td>
                          <td className="p-3 font-bold text-emerald-600">{formatCurrency(payment.payment_amount)}</td>
                          <td className="p-3 text-gray-700">{payment.payment_method}</td>
                          <td className="p-3">
                            <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                              {payment.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 6: PROFILE */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm space-y-4">
              <h3 className="text-md font-bold text-gray-900">Personal & Emergency Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div><label className="text-gray-500 font-bold">Full Name</label><p className="text-gray-900 font-bold text-sm">{member.first_name} {member.last_name}</p></div>
                <div><label className="text-gray-500 font-bold">Email</label><p className="text-gray-900 font-medium">{member.email}</p></div>
                <div><label className="text-gray-500 font-bold">Phone</label><p className="text-gray-900 font-medium">{member.phone}</p></div>
                <div><label className="text-gray-500 font-bold">Member ID</label><p className="text-gray-900 font-mono font-bold">{member.member_id}</p></div>
                <div><label className="text-gray-500 font-bold">Date of Birth</label><p className="text-gray-900">{formatDate(member.date_of_birth)}</p></div>
                <div><label className="text-gray-500 font-bold">Gender</label><p className="text-gray-900">{member.gender || '-'}</p></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
